import { useQuery } from '@tanstack/react-query';
import {
  CONTEXT_SECTION_TITLES,
  type ContextSection,
  type CriterionOutcome,
  type TestResult,
} from '@lixionary-jira/domain';
import { CircleCheck, CircleDashed } from 'lucide-react';
import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { api } from '../../api/client';
import { useAction } from '../../api/hooks';
import { useIsAdmin } from '../../api/session';
import type { ActorRef } from '../../api/types';
import { useCrumbs } from '../../layout/crumbs';
import { Badge, StateBadge } from '../../ui/Badge';
import { Button } from '../../ui/Button';
import { Alert, ErrorAlert, QueryView } from '../../ui/feedback';
import { Field, Input, Select, Textarea } from '../../ui/forms';
import { Markdown } from '../../ui/Markdown';
import { Modal } from '../../ui/Modal';
import { ActorName, Card, KeyValue, PageHeader, usePageTitle } from '../../ui/misc';
import { Timestamp, age } from '../../ui/Timestamp';

interface Evidence {
  revision: number;
  summary: string;
  sourceRevision: string;
  branch?: string | null;
  pullRequest?: string | null;
  worktree?: string | null;
  criteriaResults: { criterionIndex: number; outcome: CriterionOutcome; observation: string }[];
  tests: { name: string; command: string; result: TestResult; evidenceRef?: string | null }[];
  knownLimitations: string;
  deploymentConsiderations: string;
  rollbackConsiderations: string;
  risks?: string | null;
  contextVersions: Record<string, string | null>;
  recordedAt: string;
}

interface ImplDetail {
  id: string;
  displayId: string;
  ticket: {
    key: string;
    number: number;
    title: string;
    status: string;
    version: number;
    acceptanceCriteria: string[];
    scopeRevision: number;
  };
  project: { key: string; name: string; archived: boolean };
  state: string;
  ownerKind: 'ai' | 'human';
  owner: ActorRef | null;
  generation: number;
  scopeRevision: number;
  acceptance: {
    id: string;
    displayId: string;
    scopeRevision: number;
    admin: ActorRef | null;
    revokedAt: string | null;
  } | null;
  builder: { id: string; name: string; machineId: string; basePath: string } | null;
  contextVersions: {
    section: ContextSection;
    versionId: string | null;
    number: number | null;
    currentVersionId: string | null;
    currentNumber: number | null;
    current: boolean;
  }[];
  workspace: {
    branch: string | null;
    baseRevision: string | null;
    worktree: string | null;
    workDirectory: string | null;
    provenance: string;
  };
  retainedFrom: string | null;
  phase: string | null;
  startedAt: string;
  lastHeartbeatAt: string | null;
  leaseExpiresAt: string | null;
  endedAt: string | null;
  terminationReason: string | null;
  recovery: {
    choice: string;
    workspaceAssessment: string;
    reason: string;
    at: string;
    admin: ActorRef | null;
  } | null;
  progress: { id: string; kind: string; phase: string | null; note: string; createdAt: string }[];
  evidence: Evidence | null;
  evidenceHistory: { revision: number; sourceRevision: string; createdAt: string }[];
  exceptions: {
    id: string;
    testName: string;
    reason: string;
    admin: ActorRef | null;
    createdAt: string;
    revokedAt: string | null;
  }[];
  readiness: {
    id: string;
    createdAt: string;
    invalidatedAt: string | null;
    invalidatedReason: string | null;
    sourceRevision: string;
  }[];
  viewer: { canRecover: boolean; isManualOwner: boolean };
}

const STATE_LABEL: Record<string, string> = {
  active: 'Active',
  stale: 'Stale owner',
  ready: 'Ready',
  released: 'Released',
  recovered: 'Recovered',
  terminated: 'Terminated',
};

/** WF-07–09, AI-04–06: claim, workspace references, progress, evidence, readiness and recovery. */
export function ImplementationPage() {
  const { key, number, implementationId } = useParams();
  const q = useQuery({
    queryKey: ['implementation', key, number, implementationId],
    queryFn: () =>
      api.get<ImplDetail>(`/projects/${key}/tickets/${number}/implementations/${implementationId}`),
  });
  usePageTitle(q.data ? `${q.data.ticket.key} · Implementation ${q.data.displayId}` : null);
  useCrumbs([
    { label: q.data?.project.name ?? key ?? '', to: `/projects/${key}` },
    { label: q.data?.ticket.key ?? `${key}-${number}`, to: `/projects/${key}/tickets/${number}` },
    { label: `Implementation ${implementationId}` },
  ]);
  const [dialog, setDialog] = useState<'recover' | 'evidence' | 'exception' | null>(null);
  const admin = useIsAdmin();
  const inv = [
    ['implementation', key, number],
    ['ticket', key, Number(number)],
    ['board', key],
  ];
  const ready = useAction(
    () =>
      api.post(`/projects/${key}/tickets/${number}/implementation/ready`, {
        expectedVersion: q.data!.ticket.version,
      }),
    { success: 'Moved to Pending release', invalidate: inv },
  );
  return (
    <div className="page">
      <QueryView query={q}>
        {(i) => {
          const criteria = i.ticket.acceptanceCriteria;
          const ev = i.evidence;
          const contextOk = i.contextVersions
            .filter((c) => c.section !== 'deployment')
            .every((c) => !c.currentVersionId || c.current);
          const checks: [string, boolean, string][] = [
            [
              `Current acceptance (scope rev ${i.ticket.scopeRevision})`,
              !!i.acceptance && !i.acceptance.revokedAt && i.scopeRevision === i.ticket.scopeRevision,
              i.acceptance?.displayId ?? 'None',
            ],
            [
              'Context acknowledged',
              contextOk,
              i.contextVersions
                .map((c) => `${CONTEXT_SECTION_TITLES[c.section]} ${c.number ? `v${c.number}` : '—'}`)
                .join(' · '),
            ],
            [
              'Exact revision recorded',
              !!ev && /^[0-9a-f]{7,64}$/i.test(ev.sourceRevision),
              ev?.sourceRevision.slice(0, 12) ?? 'Missing',
            ],
            ...criteria.map((c, idx): [string, boolean, string] => {
              const r = ev?.criteriaResults.find((x) => x.criterionIndex === idx);
              return [
                `Criterion ${idx + 1}: ${c}`,
                !!r && r.outcome !== 'not_met' && !!r.observation.trim(),
                r ? r.outcome.replace('_', ' ') : 'Missing evidence',
              ];
            }),
            [
              'Tests or approved exceptions',
              !!ev &&
                (ev.tests.length > 0 || i.exceptions.some((e) => e.testName === '*')) &&
                ev.tests.every(
                  (t) =>
                    t.result === 'passed' || i.exceptions.some((e) => e.testName === t.name && !e.revokedAt),
                ),
              ev
                ? `${ev.tests.filter((t) => t.result === 'passed').length}/${ev.tests.length} passed`
                : 'Missing',
            ],
            [
              'Delivery and rollback considerations',
              !!ev && !!ev.deploymentConsiderations.trim() && !!ev.rollbackConsiderations.trim(),
              ev ? 'Recorded' : 'Missing',
            ],
          ];
          return (
            <>
              <PageHeader
                title={`Implementation ${i.displayId}`}
                subtitle={
                  <>
                    <Link to={`/projects/${key}/tickets/${number}`}>{i.ticket.key}</Link> · {i.ticket.title}
                  </>
                }
                actions={
                  <>
                    <StateBadge state={i.state} label={STATE_LABEL[i.state]} />
                    {i.viewer.canRecover && (
                      <Button
                        variant={i.state === 'stale' ? 'primary' : 'secondary'}
                        size="sm"
                        onClick={() => setDialog('recover')}
                      >
                        Recover claim
                      </Button>
                    )}
                    {admin && (i.state === 'active' || i.state === 'stale') && (
                      <Button size="sm" variant="secondary" onClick={() => setDialog('exception')}>
                        Approve exception
                      </Button>
                    )}
                    {i.viewer.isManualOwner && (
                      <>
                        <Button size="sm" variant="secondary" onClick={() => setDialog('evidence')}>
                          Record evidence
                        </Button>
                        <Button size="sm" onClick={() => ready.mutate(undefined)} loading={ready.isPending}>
                          Mark pending release
                        </Button>
                      </>
                    )}
                  </>
                }
              />
              {i.state === 'stale' && (
                <Alert tone="error" title="Stale owner — work writes are fenced">
                  <span>
                    No heartbeat since <Timestamp value={i.lastHeartbeatAt} /> (
                    {i.lastHeartbeatAt ? age(i.lastHeartbeatAt) : 'never'}). The lease expired at{' '}
                    <Timestamp value={i.leaseExpiresAt} />. Check the external workspace before recovering;
                    the application cannot stop a process that still runs on{' '}
                    {i.builder?.machineId ?? 'the builder'}.
                  </span>
                </Alert>
              )}
              <ErrorAlert error={ready.error} title="Readiness was refused" />
              <div className="split">
                <div className="stack">
                  <Card
                    title="Readiness checklist"
                    subtitle="All items are required before Pending release. Readiness does not authorize deployment."
                  >
                    <ul className="checklist">
                      {checks.map(([label, ok, note]) => (
                        <li key={label}>
                          {ok ? (
                            <CircleCheck size={16} color="var(--color-success)" aria-label="Complete" />
                          ) : (
                            <CircleDashed size={16} color="var(--color-muted-soft)" aria-label="Incomplete" />
                          )}
                          <span style={{ flex: 1 }}>{label}</span>
                          <span className="small muted">{note}</span>
                        </li>
                      ))}
                    </ul>
                  </Card>
                  <Card
                    title="Evidence"
                    subtitle={
                      ev
                        ? `Revision ${ev.revision} · recorded ${new Date(ev.recordedAt).toLocaleString()} · supplied by the owner`
                        : 'No evidence recorded yet.'
                    }
                  >
                    {ev && (
                      <>
                        <Markdown>{ev.summary}</Markdown>
                        <KeyValue
                          items={[
                            [
                              'Source revision',
                              <span key="s" className="mono">
                                {ev.sourceRevision}
                              </span>,
                            ],
                            ['Branch', ev.branch ?? '—'],
                            [
                              'Pull request',
                              ev.pullRequest ? (
                                <a href={ev.pullRequest} target="_blank" rel="noopener noreferrer nofollow">
                                  {ev.pullRequest}
                                </a>
                              ) : (
                                '—'
                              ),
                            ],
                          ]}
                        />
                        <span className="small strong">Criteria results</span>
                        <ul className="checklist">
                          {criteria.map((c, idx) => {
                            const r = ev.criteriaResults.find((x) => x.criterionIndex === idx);
                            return (
                              <li key={idx} style={{ flexDirection: 'column', gap: 2 }}>
                                <span>
                                  {idx + 1}. {c}{' '}
                                  {r ? (
                                    <Badge
                                      tone={
                                        r.outcome === 'met'
                                          ? 'success'
                                          : r.outcome === 'not_met'
                                            ? 'error'
                                            : 'default'
                                      }
                                    >
                                      {r.outcome.replace('_', ' ')}
                                    </Badge>
                                  ) : (
                                    <Badge tone="warning">Missing</Badge>
                                  )}
                                </span>
                                {r?.observation && <span className="small muted">{r.observation}</span>}
                              </li>
                            );
                          })}
                        </ul>
                        <span className="small strong">Tests and checks</span>
                        <div className="table-wrap">
                          <table className="table">
                            <thead>
                              <tr>
                                <th>Name</th>
                                <th>Command</th>
                                <th>Result</th>
                              </tr>
                            </thead>
                            <tbody>
                              {ev.tests.map((t) => (
                                <tr key={t.name}>
                                  <td className="mono small">{t.name}</td>
                                  <td className="mono small">{t.command}</td>
                                  <td>
                                    <Badge
                                      tone={
                                        t.result === 'passed'
                                          ? 'success'
                                          : t.result === 'failed'
                                            ? 'error'
                                            : 'default'
                                      }
                                    >
                                      {t.result.replace('_', ' ')}
                                    </Badge>
                                    {i.exceptions.some((e) => e.testName === t.name && !e.revokedAt) && (
                                      <Badge tone="info">Exception approved</Badge>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        {ev.knownLimitations && (
                          <>
                            <span className="small strong">Known limitations</span>
                            <Markdown>{ev.knownLimitations}</Markdown>
                          </>
                        )}
                        <span className="small strong">Deployment and verification considerations</span>
                        <Markdown>{ev.deploymentConsiderations}</Markdown>
                        <span className="small strong">Rollback considerations</span>
                        <Markdown>{ev.rollbackConsiderations}</Markdown>
                        {ev.risks && (
                          <>
                            <span className="small strong">Risks</span>
                            <Markdown>{ev.risks}</Markdown>
                          </>
                        )}
                      </>
                    )}
                  </Card>
                  <Card title="Progress">
                    {i.progress.length === 0 ? (
                      <span className="small muted">No progress recorded.</span>
                    ) : (
                      <div className="timeline">
                        {i.progress.map((p) => (
                          <div key={p.id} className="timeline-item">
                            <span className="small muted">
                              <Timestamp value={p.createdAt} />
                            </span>
                            <span>
                              {p.kind === 'blocker' && <Badge tone="error">Blocker</Badge>}{' '}
                              {p.phase && <span className="strong">{p.phase}: </span>}
                              <span style={{ whiteSpace: 'pre-wrap' }}>{p.note}</span>
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </Card>
                </div>
                <div className="stack">
                  <Card title="Claim" tight>
                    <KeyValue
                      items={[
                        [
                          'Owner',
                          <span key="o">
                            <ActorName actor={i.owner} />
                          </span>,
                        ],
                        ['Generation', i.generation],
                        [
                          'Builder',
                          i.builder
                            ? `${i.builder.name} · ${i.builder.machineId}`
                            : i.ownerKind === 'human'
                              ? 'Manual'
                              : '—',
                        ],
                        [
                          'Acceptance',
                          i.acceptance
                            ? `${i.acceptance.displayId} · scope rev ${i.acceptance.scopeRevision}`
                            : '—',
                        ],
                        ['Started', <Timestamp key="s" value={i.startedAt} />],
                        [
                          'Last heartbeat',
                          i.ownerKind === 'ai' ? (
                            <Timestamp value={i.lastHeartbeatAt} />
                          ) : (
                            'Not used for manual work'
                          ),
                        ],
                        [
                          'Lease expires',
                          i.ownerKind === 'ai' ? <Timestamp value={i.leaseExpiresAt} /> : '—',
                        ],
                        ['Ended', <Timestamp key="e" value={i.endedAt} />],
                        ['Termination', i.terminationReason ?? '—'],
                      ]}
                    />
                  </Card>
                  <Card title="Context versions used" tight>
                    <ul className="checklist">
                      {i.contextVersions.map((c) => (
                        <li key={c.section} style={{ justifyContent: 'space-between' }}>
                          <span>{CONTEXT_SECTION_TITLES[c.section]}</span>
                          {c.number ? (
                            <Link
                              to={`/projects/${key}/context/${c.section}/versions/${c.number}`}
                              className="mono"
                            >
                              v{c.number}
                            </Link>
                          ) : (
                            <span className="muted">—</span>
                          )}
                          {!c.current && c.currentNumber && (
                            <Badge tone="warning">Current v{c.currentNumber}</Badge>
                          )}
                        </li>
                      ))}
                    </ul>
                  </Card>
                  <Card
                    title="Workspace (reported)"
                    tight
                    subtitle="Reported metadata; the server cannot inspect the machine."
                  >
                    <KeyValue
                      items={[
                        [
                          'Branch',
                          <span key="b" className="mono small">
                            {i.workspace.branch ?? '—'}
                          </span>,
                        ],
                        [
                          'Base revision',
                          <span key="r" className="mono small">
                            {i.workspace.baseRevision ?? '—'}
                          </span>,
                        ],
                        [
                          'Worktree',
                          <span key="w" className="mono small">
                            {i.workspace.worktree ?? '—'}
                          </span>,
                        ],
                        [
                          'Directory',
                          <span key="d" className="mono small">
                            {i.workspace.workDirectory ?? '—'}
                          </span>,
                        ],
                        ['Retained from', i.retainedFrom ?? '—'],
                      ]}
                    />
                  </Card>
                  {i.recovery && (
                    <Card title="Recovery" tight>
                      <KeyValue
                        items={[
                          ['Decision', i.recovery.choice],
                          ['By', <ActorName key="a" actor={i.recovery.admin} />],
                          ['When', <Timestamp key="w" value={i.recovery.at} />],
                          ['Workspace', i.recovery.workspaceAssessment],
                          ['Reason', i.recovery.reason],
                        ]}
                      />
                    </Card>
                  )}
                  {i.exceptions.length > 0 && (
                    <Card title="Approved exceptions" tight>
                      <ul className="checklist">
                        {i.exceptions.map((e) => (
                          <li key={e.id} className="small">
                            {e.testName === '*' ? 'No tests' : e.testName}: “{e.reason}” —{' '}
                            <ActorName actor={e.admin} />
                          </li>
                        ))}
                      </ul>
                    </Card>
                  )}
                </div>
              </div>
              {dialog === 'recover' && <RecoverDialog i={i} onClose={() => setDialog(null)} inv={inv} />}
              {dialog === 'exception' && <ExceptionDialog i={i} onClose={() => setDialog(null)} inv={inv} />}
              {dialog === 'evidence' && <EvidenceDialog i={i} onClose={() => setDialog(null)} inv={inv} />}
            </>
          );
        }}
      </QueryView>
    </div>
  );
}

function RecoverDialog({ i, onClose, inv }: { i: ImplDetail; onClose: () => void; inv: unknown[][] }) {
  const [choice, setChoice] = useState<'manual' | 'todo' | 'pending'>('manual');
  const [assessment, setAssessment] = useState('');
  const [reason, setReason] = useState('');
  const m = useAction(
    () =>
      api.post(`/projects/${i.project.key}/tickets/${i.ticket.number}/implementation/recover`, {
        expectedVersion: i.ticket.version,
        choice,
        workspaceAssessment: assessment,
        reason,
      }),
    { success: 'Claim recovered', invalidate: inv as never, onSuccess: onClose },
  );
  const options: [typeof choice, string, string][] = [
    [
      'manual',
      'Take manual ownership',
      `You own ${i.ticket.key} in In progress; it is no longer AI-claimable.`,
    ],
    [
      'todo',
      'Return to Todo',
      'Scope is unchanged; any eligible builder can claim it again with a new generation.',
    ],
    ['pending', 'Return to Pending', 'Use if scope changed; fresh acceptance is required.'],
  ];
  return (
    <Modal
      title={`Recover ${i.displayId}`}
      onClose={onClose}
      wide
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => m.mutate(undefined)}
            loading={m.isPending}
            disabled={!assessment.trim() || !reason.trim()}
          >
            Recover claim
          </Button>
        </>
      }
    >
      <Alert tone="warning">
        Recovery fences the old generation inside the application. It does not stop an external process;
        confirm the workspace state first.
      </Alert>
      <div className="stack sm" role="radiogroup" aria-label="Recovery choice">
        {options.map(([v, label, d]) => (
          <label
            key={v}
            className="check"
            style={{
              border: `1px solid ${choice === v ? 'var(--color-primary)' : 'var(--color-hairline)'}`,
              borderRadius: 8,
              padding: 12,
            }}
          >
            <input type="radio" name="recover" checked={choice === v} onChange={() => setChoice(v)} />
            <span className="stack" style={{ gap: 2 }}>
              <span className="strong">{label}</span>
              <span className="small muted">{d}</span>
            </span>
          </label>
        ))}
      </div>
      <Field
        label="External workspace assessment"
        hint="What you found on the builder: processes, branch state, uncommitted work."
      >
        {(p) => (
          <Textarea {...p} rows={3} value={assessment} onChange={(e) => setAssessment(e.target.value)} />
        )}
      </Field>
      <Field label="Reason">
        {(p) => <Input {...p} value={reason} onChange={(e) => setReason(e.target.value)} />}
      </Field>
      <ErrorAlert error={m.error} />
    </Modal>
  );
}

function ExceptionDialog({ i, onClose, inv }: { i: ImplDetail; onClose: () => void; inv: unknown[][] }) {
  const tests = i.evidence?.tests.filter((t) => t.result !== 'passed').map((t) => t.name) ?? [];
  const [testName, setTestName] = useState(tests[0] ?? '*');
  const [reason, setReason] = useState('');
  const m = useAction(
    () =>
      api.post(`/projects/${i.project.key}/tickets/${i.ticket.number}/implementation/exceptions`, {
        testName,
        reason,
      }),
    { success: 'Exception approved', invalidate: inv as never, onSuccess: onClose },
  );
  return (
    <Modal
      title="Approve readiness exception"
      onClose={onClose}
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => m.mutate(undefined)} loading={m.isPending} disabled={!reason.trim()}>
            Approve exception
          </Button>
        </>
      }
    >
      <p className="muted">
        Expected checks that could not run need your explicit exception. It is recorded under your name and
        shown on release review.
      </p>
      <Field label="Check">
        {(p) => (
          <Select {...p} value={testName} onChange={(e) => setTestName(e.target.value)}>
            {tests.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
            <option value="*">No tests could run at all</option>
          </Select>
        )}
      </Field>
      <Field label="Reason">
        {(p) => <Textarea {...p} rows={3} value={reason} onChange={(e) => setReason(e.target.value)} />}
      </Field>
      <ErrorAlert error={m.error} />
    </Modal>
  );
}

function EvidenceDialog({ i, onClose, inv }: { i: ImplDetail; onClose: () => void; inv: unknown[][] }) {
  const prev = i.evidence;
  const [summary, setSummary] = useState(prev?.summary ?? '');
  const [rev, setRev] = useState(prev?.sourceRevision ?? '');
  const [branch, setBranch] = useState(prev?.branch ?? '');
  const [pr, setPr] = useState(prev?.pullRequest ?? '');
  const [results, setResults] = useState(
    i.ticket.acceptanceCriteria.map(
      (_, idx) =>
        prev?.criteriaResults.find((r) => r.criterionIndex === idx) ?? {
          criterionIndex: idx,
          outcome: 'met' as CriterionOutcome,
          observation: '',
        },
    ),
  );
  const [tests, setTests] = useState(
    prev?.tests.map((t) => `${t.name} | ${t.command} | ${t.result}`).join('\n') ?? '',
  );
  const [limitations, setLimitations] = useState(prev?.knownLimitations ?? '');
  const [deploy, setDeploy] = useState(prev?.deploymentConsiderations ?? '');
  const [rollback, setRollback] = useState(prev?.rollbackConsiderations ?? '');
  const m = useAction(
    () =>
      api.post(`/projects/${i.project.key}/tickets/${i.ticket.number}/implementation/evidence`, {
        expectedVersion: i.ticket.version,
        evidence: {
          summary,
          sourceRevision: rev,
          branch: branch || null,
          pullRequest: pr || null,
          criteriaResults: results,
          tests: tests
            .split('\n')
            .map((l) => l.split('|').map((x) => x.trim()))
            .filter((p) => p[0])
            .map(([name, command, result]) => ({
              name: name!,
              command: command ?? '',
              result: (['passed', 'failed', 'not_run'].includes(result ?? '')
                ? result
                : 'passed') as TestResult,
            })),
          knownLimitations: limitations,
          deploymentConsiderations: deploy,
          rollbackConsiderations: rollback,
          contextVersions: Object.fromEntries(i.contextVersions.map((c) => [c.section, c.currentVersionId])),
        },
      }),
    { success: 'Evidence recorded', invalidate: inv as never, onSuccess: onClose },
  );
  return (
    <Modal
      title="Record implementation evidence"
      onClose={onClose}
      wide
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => m.mutate(undefined)} loading={m.isPending}>
            Save evidence
          </Button>
        </>
      }
    >
      <Field label="Implementation summary">
        {(p) => <Textarea {...p} rows={3} value={summary} onChange={(e) => setSummary(e.target.value)} />}
      </Field>
      <div className="grid-2">
        <Field label="Exact source revision (commit SHA)">
          {(p) => <Input {...p} className="mono" value={rev} onChange={(e) => setRev(e.target.value)} />}
        </Field>
        <Field label="Branch" optional>
          {(p) => (
            <Input {...p} className="mono" value={branch} onChange={(e) => setBranch(e.target.value)} />
          )}
        </Field>
      </div>
      <Field label="Pull request" optional>
        {(p) => <Input {...p} value={pr} onChange={(e) => setPr(e.target.value)} />}
      </Field>
      {i.ticket.acceptanceCriteria.map((c, idx) => (
        <div key={idx} className="grid-2">
          <Field label={`Criterion ${idx + 1}: ${c}`}>
            {(p) => (
              <Select
                {...p}
                value={results[idx]!.outcome}
                onChange={(e) =>
                  setResults(
                    results.map((r, j) =>
                      j === idx ? { ...r, outcome: e.target.value as CriterionOutcome } : r,
                    ),
                  )
                }
              >
                <option value="met">Met</option>
                <option value="not_met">Not met</option>
                <option value="not_applicable">Not applicable</option>
              </Select>
            )}
          </Field>
          <Field label="Observation">
            {(p) => (
              <Input
                {...p}
                value={results[idx]!.observation}
                onChange={(e) =>
                  setResults(results.map((r, j) => (j === idx ? { ...r, observation: e.target.value } : r)))
                }
              />
            )}
          </Field>
        </div>
      ))}
      <Field label="Tests" hint="One per line: name | command | passed, failed or not_run">
        {(p) => <Textarea {...p} mono rows={4} value={tests} onChange={(e) => setTests(e.target.value)} />}
      </Field>
      <Field label="Known limitations" optional>
        {(p) => (
          <Textarea {...p} rows={2} value={limitations} onChange={(e) => setLimitations(e.target.value)} />
        )}
      </Field>
      <Field label="Deployment and verification considerations">
        {(p) => <Textarea {...p} rows={2} value={deploy} onChange={(e) => setDeploy(e.target.value)} />}
      </Field>
      <Field label="Rollback considerations">
        {(p) => <Textarea {...p} rows={2} value={rollback} onChange={(e) => setRollback(e.target.value)} />}
      </Field>
      <p className="small muted">Saving acknowledges the current context versions shown on this page.</p>
      <ErrorAlert error={m.error} />
    </Modal>
  );
}
